import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { lookupIdMatcher } from 'utils/matchers';
import * as actions from '../../components/MyProfile/redux/actions';
import useActions from './useActions';
import useIsUserAuthenticated from './useIsUserAuthenticated';

const MAX_FETCH_ATTEMPTS = 3;
let bIsFetching = false;

/**
 * @description - Fetches profile data via Redux action
 * @param {string} sLookupId - Lookup ID of user to fetch profile data for
 *
 * @returns {bool} - If profile data is done loading or not
 */
const useProfileData = (sLookupId = 'self') => {
  const profileActions = useActions(actions);
  const profile = useSelector((state) => state.Profile);
  const [isLoading, setIsLoading] = useState(profile === null);
  const bIsUserAuthenticated = useIsUserAuthenticated();
  const bIsLookupIdValid =
    lookupIdMatcher.test(sLookupId) || sLookupId === 'self';

  useEffect(() => {
    if (
      bIsUserAuthenticated === true &&
      profile === null &&
      !bIsFetching &&
      bIsLookupIdValid
    ) {
      bIsFetching = true;

      const attemptProfileDataFetch = async (attempt = 1) => {
        try {
          await new Promise((resolve, reject) => {
            profileActions.get({
              data: {
                lookupId: sLookupId,
                bIsUserAuthenticated,
              },
              resolve,
              reject,
            });
          });
        } catch (err) {
          if (attempt < MAX_FETCH_ATTEMPTS) {
            const delay = 2 ** (attempt - 1) * 1000; // Exponential delay for retry: 1s, 2s, 4s
            await new Promise((resolve) => {
              setTimeout(resolve, delay);
            });
            return attemptProfileDataFetch(attempt + 1);
          }
          console.error('Error loading profile data', err);
          return null;
        }
      };

      attemptProfileDataFetch().finally(() => {
        setIsLoading(false);
      });
    } else if (profile || !bIsLookupIdValid) {
      setIsLoading(false);
    }
  }, [
    profile,
    profileActions,
    bIsUserAuthenticated,
    sLookupId,
    bIsLookupIdValid,
  ]);

  return isLoading;
};

export default useProfileData;
